import { createClient, type SupabaseClient } from '@supabase/supabase-js';
import type { ProviderInterface, UserRole, Session, Household, HouseholdMember, Quest, QuestAssignment, QuestCompletion, LedgerEntry, Reward, RewardRedemption, KidHistoryItem, KidWish } from './types';

export class SupabaseProvider implements ProviderInterface {
    private supabase: SupabaseClient;

    constructor(url: string, key: string) {
        this.supabase = createClient(url, key);
    }

    async getSession(): Promise<Session> {
        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) return { user: null, isAuthenticated: false };

        const { data: member, error: memberError } = await this.supabase
            .from('household_members')
            .select('*, household:households!inner(language)')
            .eq('user_id', session.user.id)
            .single();

        if (memberError || !member) {
            return { user: null, isAuthenticated: true };
        }

        return {
            user: {
                id: session.user.id,
                role: member.role,
                household_id: member.household_id,
                display_name: member.display_name,
                language: (member as any).language || (member as any).household.language,
            },
            isAuthenticated: true,
        };
    }

    async signIn(email: string, pass: string): Promise<Session> {
        const { data, error } = await this.supabase.auth.signInWithPassword({ email, password: pass });
        if (error) throw error;
        if (!data.session) return { user: null, isAuthenticated: false };
        return this.getSession();
    }

    async signUp(email: string, pass: string): Promise<void> {
        const { error } = await this.supabase.auth.signUp({ email, password: pass });
        if (error) throw error;
    }

    async createHousehold(name: string, founderName: string): Promise<Session> {
        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        console.log('Creating household for user:', session.user.id, 'Email:', session.user.email);

        const { data: hh, error: hhErr } = await this.supabase
            .from('households')
            .insert({ name, created_by: session.user.id })
            .select()
            .single();

        if (hhErr) {
            console.error('Household creation error details:', hhErr);
            throw hhErr;
        }

        const { error: memErr } = await this.supabase
            .from('household_members')
            .insert({
                household_id: hh.id,
                user_id: session.user.id,
                role: 'parent',
                display_name: founderName,
                email: session.user.email,
            });
        if (memErr) throw memErr;

        return this.getSession();
    }

    async signInMock(_role: UserRole, _householdId: string, _displayName: string): Promise<Session> {
        throw new Error('Real auth required for Supabase. Please use email/password.');
    }

    async joinHousehold(householdId: string, displayName: string, role: UserRole): Promise<Session> {
        const { data: { session } } = await this.supabase.auth.getSession();
        if (!session) throw new Error('Not authenticated');

        // Verify household exists
        const { data: household, error: hhErr } = await this.supabase
            .from('households')
            .select('id')
            .eq('id', householdId)
            .single();

        if (hhErr || !household) {
            throw new Error('Household not found. Please check the ID and try again.');
        }

        // Add user as member
        const { error: memErr } = await this.supabase
            .from('household_members')
            .insert({
                household_id: householdId,
                user_id: session.user.id,
                role,
                display_name: displayName,
                email: session.user.email,
            });

        if (memErr) throw memErr;

        return this.getSession();
    }

    async signOut(): Promise<void> {
        await this.supabase.auth.signOut();
    }

    async getHousehold(id: string): Promise<Household | null> {
        const { data, error } = await this.supabase
            .from('households')
            .select('*')
            .eq('id', id)
            .single();
        if (error) return null;
        return data;
    }

    async updateHousehold(id: string, updates: Partial<Household>): Promise<Household> {
        const { data, error } = await this.supabase
            .from('households')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async deleteManualPoints(ledgerEntryId: string): Promise<void> {
        const { data: entry, error: getErr } = await this.supabase
            .from('point_ledger')
            .select('id, ref_type')
            .eq('id', ledgerEntryId)
            .single();

        if (getErr) throw getErr;
        if (!entry) throw new Error('Ledger entry not found');
        if ((entry as any).ref_type !== 'manual') throw new Error('Only manual point entries can be deleted');

        const { error: delErr, count } = await this.supabase
            .from('point_ledger')
            .delete({ count: 'exact' })
            .eq('id', ledgerEntryId)
            .eq('ref_type', 'manual');

        if (delErr) throw delErr;
        if (count === 0) {
            throw new Error('Delete operation failed: No manual ledger entry found with the given ID or insufficient permissions.');
        }
    }

    async getMembers(householdId: string): Promise<HouseholdMember[]> {
        const { data, error } = await this.supabase
            .from('household_members')
            .select('*')
            .eq('household_id', householdId);
        if (error) return [];
        return data;
    }

    async createKidProfile(householdId: string, displayName: string): Promise<HouseholdMember> {
        const tempId = crypto.randomUUID();
        const { data, error } = await this.supabase
            .from('household_members')
            .insert({
                household_id: householdId,
                user_id: tempId,
                role: 'kid',
                display_name: displayName,
            })
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async deleteMember(householdId: string, userId: string): Promise<void> {
        const { error } = await this.supabase
            .from('household_members')
            .delete()
            .eq('household_id', householdId)
            .eq('user_id', userId);
        if (error) throw error;
    }

    async updateMemberLanguage(householdId: string, userId: string, language: 'en' | 'sl') {
        const { data, error } = await this.supabase
            .from('household_members')
            .update({ language })
            .eq('household_id', householdId)
            .eq('user_id', userId)
            .select()
            .single();
        if (error) throw error;
        return data as any;
    }

    async getQuests(householdId: string): Promise<Quest[]> {
        const { data, error } = await this.supabase
            .from('quests')
            .select('*, creator:household_members!left(display_name), assignments:quest_assignments(*)')
            .eq('household_id', householdId);

        if (error) {
            console.error('getQuests error:', error);
            return [];
        }

        return data.map((q: any) => ({
            ...q,
            creator_name: q.creator?.display_name
        }));
    }

    async createQuest(quest: Omit<Quest, 'id' | 'created_at'>): Promise<Quest> {
        const { data, error } = await this.supabase
            .from('quests')
            .insert(quest)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async updateQuest(id: string, updates: Partial<Quest>): Promise<Quest> {
        const { data, error } = await this.supabase
            .from('quests')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data as any;
    }

    async deleteQuest(id: string): Promise<void> {
        const { error } = await this.supabase
            .from('quests')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }

    async assignQuest(questId: string, kidUserId: string): Promise<void> {
        const { error } = await this.supabase
            .from('quest_assignments')
            .insert({ quest_id: questId, kid_user_id: kidUserId });
        if (error) throw error;
    }

    async unassignQuest(questId: string, kidUserId: string): Promise<void> {
        const { error } = await this.supabase
            .from('quest_assignments')
            .delete()
            .eq('quest_id', questId)
            .eq('kid_user_id', kidUserId);
        if (error) throw error;
    }

    async getAssignments(questId: string): Promise<QuestAssignment[]> {
        const { data, error } = await this.supabase
            .from('quest_assignments')
            .select('*')
            .eq('quest_id', questId);
        if (error) return [];
        return data;
    }

    async getKidQuests(kidUserId: string): Promise<(Quest & { assignment: QuestAssignment })[]> {
        const { data, error } = await this.supabase
            .from('quest_assignments')
            .select('*, quests!inner(*)')
            .eq('kid_user_id', kidUserId);
        if (error) return [];
        return data.map((d: any) => ({ ...d.quests, assignment: { quest_id: d.quest_id, kid_user_id: d.kid_user_id, created_at: d.created_at } }));
    }

    async getKidCompletions(kidUserId: string): Promise<(QuestCompletion & { quest: Quest })[]> {
        const { data, error } = await this.supabase
            .from('quest_completions')
            .select('*, quest:quests!inner(*)')
            .eq('kid_user_id', kidUserId)
            .order('completed_at', { ascending: false });
        if (error) return [];
        return data as any;
    }

    async getKidHistory(kidUserId: string): Promise<KidHistoryItem[]> {
        // Get quest completions
        const { data: completions, error: completionsError } = await this.supabase
            .from('quest_completions')
            .select('*, quest:quests!inner(*)')
            .eq('kid_user_id', kidUserId);

        // Get manual points
        const { data: manualEntries, error: manualError } = await this.supabase
            .from('point_ledger')
            .select('*')
            .eq('user_id', kidUserId)
            .eq('ref_type', 'manual');

        const historyItems: KidHistoryItem[] = [];

        // Add quest completions
        if (!completionsError && completions) {
            for (const completion of completions) {
                historyItems.push({
                    id: completion.id,
                    type: 'quest_completion',
                    title: completion.quest.title,
                    points: completion.quest.points,
                    created_at: completion.completed_at,
                    status: completion.status,
                    quest: completion.quest
                });
            }
        }

        // Add manual points
        if (!manualError && manualEntries) {
            for (const entry of manualEntries) {
                historyItems.push({
                    id: entry.id,
                    type: 'manual_points',
                    title: entry.reason,
                    points: entry.delta,
                    created_at: entry.created_at,
                    status: 'approved' // Manual points are automatically approved
                });
            }
        }

        return historyItems.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime());
    }

    async completeQuest(completion: Omit<QuestCompletion, 'id' | 'status' | 'completed_at'> & { completed_at?: string }): Promise<QuestCompletion> {
        const { data, error } = await this.supabase
            .from('quest_completions')
            .insert(completion)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async getPendingCompletions(householdId: string): Promise<(QuestCompletion & { quest: Quest; member: HouseholdMember })[]> {
        const { data, error } = await this.supabase
            .from('quest_completions')
            .select('*, quest:quests!inner(*), member:household_members!inner(*)')
            .eq('status', 'pending')
            .eq('quest.household_id', householdId);

        if (error) {
            console.error('getPendingCompletions error:', error);
            return [];
        }
        return data as any;
    }

    async getCompletions(householdId: string): Promise<(QuestCompletion & { quest: Quest; member: HouseholdMember })[]> {
        const { data, error } = await this.supabase
            .from('quest_completions')
            .select('*, quest:quests!inner(*), member:household_members!inner(*)')
            .eq('quest.household_id', householdId)
            .order('completed_at', { ascending: false });

        if (error) return [];
        return data as any;
    }

    async approveCompletion(id: string, approvedBy: string): Promise<void> {
        console.log(`SupabaseProvider: Approving completion ${id} by ${approvedBy}`);
        const { error } = await this.supabase.rpc('approve_quest_completion', {
            completion_id: id,
            approver_id: approvedBy
        });
        if (error) {
            console.error('SupabaseProvider: approveCompletion error:', error);
            throw error;
        }
        console.log('SupabaseProvider: approveCompletion success');
    }

    async rejectCompletion(id: string): Promise<void> {
        const { error } = await this.supabase
            .from('quest_completions')
            .update({ status: 'rejected' })
            .eq('id', id);
        if (error) throw error;
    }

    async deleteCompletion(id: string): Promise<void> {
        const { error } = await this.supabase.rpc('delete_quest_completion', {
            completion_id: id,
        });
        if (error) throw error;
    }

    async getLedger(userId: string): Promise<LedgerEntry[]> {
        const { data, error } = await this.supabase
            .from('point_ledger')
            .select('*')
            .eq('user_id', userId);
        if (error) return [];
        return data;
    }

    async getBalance(userId: string): Promise<number> {
        const { data, error } = await this.supabase.rpc('get_user_balance', { uid: userId });
        if (error) return 0;
        return data;
    }

    async addManualPoints(userId: string, points: number, reason: string, addedBy: string): Promise<LedgerEntry> {
        const { data: member } = await this.supabase
            .from('household_members')
            .select('household_id')
            .eq('user_id', userId)
            .single();

        if (!member) throw new Error('User not found');

        const { data: adder } = await this.supabase
            .from('household_members')
            .select('display_name')
            .eq('user_id', addedBy)
            .single();

        const fullReason = `${reason} (Added by ${adder?.display_name || 'Parent'})`;

        const { data, error } = await this.supabase
            .from('point_ledger')
            .insert({
                household_id: member.household_id,
                user_id: userId,
                delta: points,
                reason: fullReason,
                ref_type: 'manual',
                created_at: new Date().toISOString()
            })
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async getRewards(householdId: string): Promise<Reward[]> {
        const { data, error } = await this.supabase
            .from('rewards')
            .select('*, creator:household_members!left(display_name)')
            .eq('household_id', householdId);

        if (error) {
            console.error('getRewards error:', error);
            return [];
        }

        return data.map((r: any) => ({
            ...r,
            creator_name: r.creator?.display_name
        }));
    }

    async createReward(reward: Omit<Reward, 'id' | 'created_at'>): Promise<Reward> {
        const { data, error } = await this.supabase
            .from('rewards')
            .insert(reward)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async updateReward(id: string, updates: Partial<Reward>): Promise<Reward> {
        const { data, error } = await this.supabase
            .from('rewards')
            .update(updates)
            .eq('id', id)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async deleteReward(id: string): Promise<void> {
        const { error } = await this.supabase
            .from('rewards')
            .delete()
            .eq('id', id);
        if (error) throw error;
    }

    async redeemReward(redemption: Omit<RewardRedemption, 'id' | 'created_at' | 'status'>): Promise<RewardRedemption> {
        const { data, error } = await this.supabase
            .from('reward_redemptions')
            .insert(redemption)
            .select()
            .single();
        if (error) throw error;
        return data;
    }

    async getPendingRedemptions(householdId: string): Promise<(RewardRedemption & { reward: Reward; member: HouseholdMember })[]> {
        const { data, error } = await this.supabase
            .from('reward_redemptions')
            .select('*, reward:rewards!inner(*), member:household_members!inner(*)')
            .eq('status', 'pending')
            .eq('rewards.household_id', householdId);

        if (error) {
            console.error('getPendingRedemptions error:', error);
            return [];
        }
        return data as any;
    }

    async getRedemptions(householdId: string): Promise<(RewardRedemption & { reward: Reward; member: HouseholdMember })[]> {
        const { data, error } = await this.supabase
            .from('reward_redemptions')
            .select('*, reward:rewards!inner(*), member:household_members!inner(*)')
            .eq('rewards.household_id', householdId)
            .order('created_at', { ascending: false });

        if (error) {
            console.error('getRedemptions error:', error);
            return [];
        }
        return data as any;
    }

    async approveRedemption(id: string, approvedBy: string): Promise<void> {
        const { error } = await this.supabase.rpc('approve_reward_redemption', {
            redemption_id: id,
            approver_id: approvedBy
        });
        if (error) throw error;
    }

    async rejectRedemption(id: string): Promise<void> {
        const { error } = await this.supabase
            .from('reward_redemptions')
            .update({ status: 'rejected' })
            .eq('id', id);
        if (error) throw error;
    }

    async revertRedemption(id: string): Promise<void> {
        // Get the redemption details
        const { data: redemption, error: redemptionError } = await this.supabase
            .from('reward_redemptions')
            .select('*')
            .eq('id', id)
            .single();
            
        if (redemptionError) throw redemptionError;
        
        // Get the reward details
        const { data: reward, error: rewardError } = await this.supabase
            .from('rewards')
            .select('*')
            .eq('id', redemption.reward_id)
            .single();
            
        if (rewardError) throw rewardError;
        
        // Update redemption status back to pending
        const { error: updateError } = await this.supabase
            .from('reward_redemptions')
            .update({ 
                status: 'pending',
                approved_by: null,
                approved_at: null
            })
            .eq('id', id);
            
        if (updateError) throw updateError;
        
        // Return points to ledger
        const { error: ledgerError } = await this.supabase
            .from('point_ledger')
            .insert({
                household_id: reward.household_id,
                user_id: redemption.kid_user_id,
                delta: reward.cost_points,
                reason: `Reverted reward: ${reward.title}`,
                ref_type: 'reward_redemption',
                ref_id: redemption.id,
                created_at: new Date().toISOString()
            });
            
        if (ledgerError) throw ledgerError;
    }

    async deleteRedemption(id: string): Promise<void> {
        // First, try to delete any related ledger entries
        await this.supabase
            .from('point_ledger')
            .delete()
            .eq('ref_id', id)
            .eq('ref_type', 'reward_redemption');
            
        // Now delete the redemption
        const { error, count } = await this.supabase
            .from('reward_redemptions')
            .delete({ count: 'exact' })
            .eq('id', id);
            
        if (error) {
            throw error;
        }
        
        if (count === 0) {
            throw new Error('Delete operation failed: No redemption found with the given ID or insufficient permissions.');
        }
    }

    // Wishes
    async getKidWish(kidUserId: string): Promise<KidWish | null> {
        const { data, error } = await this.supabase
            .from('kid_wishes')
            .select('*')
            .eq('kid_user_id', kidUserId)
            .single();

        if (error && error.code !== 'PGRST116') { // PGRST116 = no rows returned
            throw error;
        }

        return data;
    }

    async setKidWish(wish: Omit<KidWish, 'id' | 'created_at' | 'updated_at'>): Promise<KidWish> {
        const { data, error } = await this.supabase
            .from('kid_wishes')
            .upsert(wish, { onConflict: 'kid_user_id' })
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async updateKidWish(id: string, updates: Partial<KidWish>): Promise<KidWish> {
        const { data, error } = await this.supabase
            .from('kid_wishes')
            .update(updates)
            .eq('id', id)
            .select()
            .single();

        if (error) throw error;
        return data;
    }

    async deleteKidWish(kidUserId: string): Promise<void> {
        const { error } = await this.supabase
            .from('kid_wishes')
            .delete()
            .eq('kid_user_id', kidUserId);

        if (error) throw error;
    }

    // Get the Supabase client for real-time subscriptions
    getSupabaseClient(): SupabaseClient {
        return this.supabase;
    }
}
